import { useCallback } from "react";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { MenuOptionValue, Paginated, PaginationQuery, PaginationQueryWithFields, Version, Versions } from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { Subsidiary, SubsidiaryVersion, UpsertIsoCode, UpsertSubsidiary } from "./types";

export const useSubsidiariesApi = () => {
  const api = useApi();

  const getAdminSubsidiary = useCallback(
    (subsidiaryId: string, signal: AbortSignal) => {
      const url = `/admin/subsidiaries/${subsidiaryId}`;
      return useSWRMutation<Subsidiary | null, CustomError, string>(url, (url) => api.get<Subsidiary>({ url, signal }));
    },
    [api],
  );

  const useAdminSubsidiary = useCallback(
    (subsidiaryId: string, signal: AbortSignal) => {
      const url = `/admin/subsidiaries/${subsidiaryId}`;
      return useSWR<Subsidiary | null, CustomError, string>(url, (url) => api.get<Subsidiary>({ url, signal }));
    },
    [api],
  );

  const useAdminSubsidiaries = useCallback(
    <K extends keyof Subsidiary>(
      query: PaginationQueryWithFields<Pick<Subsidiary, K>>,
      signal: AbortSignal,
      hasPermission?: boolean,
    ) => {
      const url = buildUrl<Pick<Subsidiary, K>>("/admin/subsidiaries", query);

      const key = hasPermission === false ? false : url;

      return useSWR<Paginated<Pick<Subsidiary, K>> | null, CustomError, string | null>(
        key as string | null,
        (url: string) => api.get<Paginated<Pick<Subsidiary, K>>>({ url, signal }),
      );
    },
    [api],
  );

  const useUserSubsidiaries = useCallback(
    <K extends keyof Subsidiary>(query: PaginationQueryWithFields<Pick<Subsidiary, K>>, signal: AbortSignal) => {
      const url = buildUrl<Pick<Subsidiary, K>>("/admin/users/subsidiaries", query);

      return useSWR<Paginated<Pick<Subsidiary, K>> | null, CustomError, string | null>(url, (url: string) =>
        api.get<Paginated<Pick<Subsidiary, K>>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminSubsidiaries = useCallback(
    async <K extends keyof Subsidiary>(query: PaginationQueryWithFields<Pick<Subsidiary, K>>, signal: AbortSignal) => {
      const url = buildUrl<Pick<Subsidiary, K>>("/admin/subsidiaries", query);
      const x = await api.get<Paginated<Pick<Subsidiary, K>>>({ url, signal });
      return x.items;
    },
    [api],
  );

  const insertAdminSubsidiary = useCallback(
    async (body: UpsertSubsidiary): Promise<{ id: string }> => {
      const url = `/admin/subsidiaries`;
      const id = await api.post<UpsertSubsidiary, { id: string }>(url, body);
      api.mutateCache([url]);
      return id;
    },
    [api],
  );

  const updateAdminSubsidiary = useCallback(
    async (subsidiaryId: string, body: UpsertSubsidiary): Promise<void> => {
      const url = `/admin/subsidiaries/${subsidiaryId}`;
      (await api.put<UpsertSubsidiary>(url, body),
        api.mutateCache([
          url,
          "/admin/subsidiaries",
          `/admin/subsidiaries/${subsidiaryId}/versions`,
          `/admin/subsidiaries/${subsidiaryId}/default_tasks`,
        ]));
    },
    [api],
  );

  const deleteAdminSubsidiary = useCallback(
    async (subsidiaryId: string): Promise<void> => {
      const url = `/admin/subsidiaries/${subsidiaryId}`;
      await api.del(url).then(() => {
        api.mutateCache([`/admin/subsidiaries`]);
      });
    },
    [api],
  );

  const getAdminSubsidiaryHistory = useCallback(
    (query: PaginationQuery, subsidiaryID: string, signal: AbortSignal) => {
      const url = buildUrl(`/admin/subsidiaries/${subsidiaryID}/versions`, query);
      return useSWRImmutable<Paginated<Version> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<Version>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminSubsidiaryVersion = useCallback(
    (subsidiaryId: string, versionNumber: number, signal: AbortSignal) => {
      const url = `/admin/subsidiaries/${subsidiaryId}/versions/${versionNumber}`;
      return useSWRImmutable<Versions<SubsidiaryVersion> | null, CustomError, string>(url, (url) =>
        api.get<Versions<SubsidiaryVersion>>({ url, signal }),
      );
    },
    [api],
  );

  const getFieldFilters = useCallback(
    (fieldName: string, signal: AbortSignal) => {
      const url = `/admin/subsidiaries/filters?field=${fieldName}`;
      return useSWRMutation<MenuOptionValue[] | null, CustomError, string>(url, (url) =>
        api.get<MenuOptionValue[]>({ url, signal }),
      );
    },
    [api],
  );

  const setIsoCode = useCallback(
    async (subsidiaryId: string, body: UpsertIsoCode): Promise<void> => {
      const url = `/admin/subsidiaries/${subsidiaryId}/set_iso_code`;
      (await api.put<UpsertIsoCode>(url, body),
        api.mutateCache([url, "/admin/subsidiaries", `/admin/subsidiaries/${subsidiaryId}/versions`]));
    },
    [api],
  );

  return {
    getAdminSubsidiary,
    useAdminSubsidiary,
    useAdminSubsidiaries,
    insertAdminSubsidiary,
    updateAdminSubsidiary,
    deleteAdminSubsidiary,
    getAdminSubsidiaryHistory,
    getAdminSubsidiaryVersion,
    getFieldFilters,
    getAdminSubsidiaries,
    setIsoCode,
    useUserSubsidiaries,
  };
};
